import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { api, money } from "../lib/api";
import Reveal from "../components/Reveal";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [setBundle, setSetBundle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setSetBundle(null);
    setNotFound(false);
    api.get(`/products/${id}`)
      .then(async (r) => {
        setProduct(r.data);
        if (r.data.set_id) {
          const [setRes, prodRes] = await Promise.all([api.get(`/sets/${r.data.set_id}`), api.get("/products")]);
          const pieces = prodRes.data.filter((p) => setRes.data.product_ids.includes(p.id));
          setSetBundle({ set: setRes.data, pieces });
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="flex min-h-[60vh] items-center justify-center bg-cream px-4">
        <div className="card-chunky max-w-md p-10 text-center">
          <p className="font-display text-3xl font-extrabold uppercase">Gone.</p>
          <p className="mt-2 text-sm text-charcoal/70">This piece already found a new home. Vintage waits for no one.</p>
          <Link to="/shop" className="btn-chunky mt-6 bg-blue px-6 py-3 text-sm text-white">Back to shop</Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div data-testid="product-loading" className="flex min-h-[60vh] items-center justify-center bg-cream font-mono text-sm">Loading...</div>;
  }

  const currentIndex = setBundle ? setBundle.pieces.findIndex((p) => p.id === product.id) : -1;

  return (
    <div data-testid="product-page" className="bg-cream pb-24 md:pb-0">
      {/* SPOTLIGHT */}
      <section className="border-b-2 border-charcoal">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div data-testid="product-image-tile" className="card-chunky overflow-hidden border-4 shadow-hard-lg" style={{ backgroundColor: product.tile_color }}>
              <img src={product.image} alt={product.name} className="aspect-square w-full object-cover" />
            </div>
            <div className="mt-4 flex gap-2">
              {product.rare && (
                <span className="-rotate-3 rounded-lg border-2 border-dashed border-charcoal bg-yellow px-3 py-1 font-display text-xs font-extrabold uppercase shadow-hard-sm">Limited</span>
              )}
              {product.staff_pick && (
                <span className="rotate-2 rounded-lg border-2 border-charcoal bg-blue px-3 py-1 font-display text-xs font-extrabold uppercase text-white shadow-hard-sm">Staff Pick</span>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }}>
            <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs font-bold uppercase text-charcoal/60 hover:text-blue">
              <ArrowLeft className="h-4 w-4" /> All products
            </Link>
            <p className="mt-4 font-mono text-xs font-bold uppercase tracking-wide text-charcoal/60">
              {product.brand} / {product.era} / {product.category}
            </p>
            <h1 data-testid="product-name" className="mt-2 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              {product.name}
            </h1>
            <p className="mt-4 max-w-md text-base text-charcoal/80 md:text-lg">{product.tagline}</p>
            <p data-testid="product-price" className="mt-6 font-display text-5xl font-extrabold">{money(product.price)}</p>

            {/* STAT LINE */}
            <div data-testid="product-stat" className="mt-6 rounded-2xl border-2 border-charcoal bg-white p-4 shadow-hard-sm">
              <p className="font-mono text-xs leading-relaxed">
                <span className="font-bold text-coral">[{product.stat_value}]</span> {product.stat_label}
                <span className="text-charcoal/50"> — src: {product.stat_source}</span>
              </p>
            </div>

            {/* DISCLOSURE + BUY */}
            <p data-testid="product-disclosure" className="mt-6 font-mono text-[11px] leading-relaxed text-charcoal/60">
              AFFILIATE LINK: we may earn a commission if you buy through this link, at no extra cost to you.
              Price shown is a guide — check {product.retailer} for the current price and condition.
            </p>
            <a
              href={product.affiliate_url}
              target="_blank"
              rel="sponsored noopener"
              data-testid="product-buy-button"
              className="btn-chunky mt-4 w-full bg-blue px-8 py-5 text-xl text-white hover:bg-coral sm:w-auto"
            >
              Buy on {product.retailer} <ExternalLink className="h-5 w-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* WHY IT DIED / WHY IT'S BACK */}
      <section className="border-b-2 border-charcoal bg-white py-14">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 md:grid-cols-2">
          <Reveal>
            <div data-testid="why-died" className="card-chunky h-full overflow-hidden">
              <div className="border-b-2 border-charcoal bg-coral px-6 py-4">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Why it died</h2>
              </div>
              <p className="p-6 text-base leading-relaxed text-charcoal/85">{product.why_died}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="why-back" className="card-chunky h-full overflow-hidden">
              <div className="border-b-2 border-charcoal bg-mint px-6 py-4">
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Why it's back</h2>
              </div>
              <p className="p-6 text-base leading-relaxed text-charcoal/85">{product.why_back}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPLETE THE SET */}
      {setBundle && setBundle.pieces.length > 1 && (
        <section data-testid="complete-the-set" className="bg-yellow py-14">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <Reveal>
              <p className="font-mono text-xs font-bold uppercase tracking-wide text-charcoal/70">Starter Pack</p>
              <h2 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
                Complete the set
              </h2>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex gap-1.5">
                  {setBundle.pieces.map((p, i) => (
                    <span
                      key={p.id}
                      className={`h-2.5 w-10 rounded-full border-2 border-charcoal ${i <= currentIndex ? "bg-coral" : "bg-white"}`}
                    />
                  ))}
                </div>
                <span data-testid="set-progress-label" className="font-mono text-xs font-bold uppercase">
                  Piece {currentIndex + 1} of {setBundle.pieces.length}
                </span>
              </div>
              <p className="mt-2 text-sm text-charcoal/70">
                Part of <Link to={`/sets/${setBundle.set.id}`} data-testid="set-module-link" className="font-bold underline hover:text-blue">{setBundle.set.name}</Link>
              </p>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {setBundle.pieces
                .filter((p) => p.id !== product.id)
                .map((p) => (
                  <div key={p.id} data-testid={`set-piece-${p.id}`} className="card-chunky flex items-center gap-4 p-4">
                    <Link to={`/product/${p.id}`} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 border-charcoal" style={{ backgroundColor: p.tile_color }}>
                      <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                    </Link>
                    <div className="min-w-0">
                      <Link to={`/product/${p.id}`} className="block truncate font-display text-sm font-extrabold uppercase hover:text-blue">{p.name}</Link>
                      <p className="font-mono text-xs text-charcoal/60">{money(p.price)}</p>
                      <a
                        href={p.affiliate_url}
                        target="_blank"
                        rel="sponsored noopener"
                        data-testid={`set-piece-buy-${p.id}`}
                        className="mt-1 inline-flex items-center gap-1 font-display text-xs font-bold uppercase text-blue hover:text-coral"
                      >
                        Buy on {p.retailer} <ArrowUpRight className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* STICKY MOBILE BUY BAR */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-charcoal bg-white p-3 md:hidden">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-display text-xl font-extrabold">{money(product.price)}</p>
            <p className="font-mono text-[10px] uppercase text-charcoal/60">on {product.retailer}</p>
          </div>
          <a
            href={product.affiliate_url}
            target="_blank"
            rel="sponsored noopener"
            data-testid="product-sticky-buy"
            className="btn-chunky flex-1 bg-blue px-6 py-3.5 text-base text-white hover:bg-coral"
          >
            Buy on {product.retailer}
          </a>
        </div>
      </div>
    </div>
  );
}
